import {useCallback, useState} from 'react';
import {SaveState} from '../shared/saveState';
import {NovaPromptState, PromptInputs, useNovaPromptState} from './useNovaPromptState';

export interface NovaAgentSavePayload extends PromptInputs {
	categoryId: string | null;
	title: string;
	description: string;
	// The model id the author chose, or null when the agent has none yet (the service then applies
	// the platform default). Part of the versioned content: the service folds it into the version
	// content hash, so changing it alone still produces a discrete version.
	model: string | null;
	// The name of the version being edited, as typed. Empty when it has none.
	versionName: string;
}

export interface UseNovaAgentDraftOptions {
	initialValues?: Partial<PromptInputs>;
	initialCategoryId?: string | null;
	initialTitle?: string;
	initialDescription?: string;
	initialModel?: string | null;
	onSave?: (values: NovaAgentSavePayload) => Promise<void>;
}

export interface NovaAgentDraft {
	promptState: NovaPromptState;
	categoryId: string | null;
	setCategoryId: (categoryId: string | null) => void;
	// The agent's display title and description are edited inline on the Prompt tab and committed
	// as part of the same working draft — there is no separate edit surface.
	title: string;
	setTitle: (title: string) => void;
	description: string;
	setDescription: (description: string) => void;
	// Which model this agent runs on. A metadata slice rather than a prompt input: the prompt-state
	// hook maps a target to a text field and splices tokens into it, and a non-text field with no
	// insertion target has no place in that contract.
	model: string | null;
	setModel: (model: string | null) => void;
	// The version name, edited on a workflow's Prompt tab and saved with the rest of the draft.
	versionName: string;
	setVersionName: (versionName: string) => void;
	isDirty: boolean;
	saveState: SaveState;
	save: () => Promise<void>;
	load: (
		inputs: PromptInputs,
		categoryId: string | null,
		title: string,
		description: string,
		model: string | null,
		versionName?: string | null
	) => void;
}

// The Prompt tab's editors, the title/description fields, and the category picker each own one
// slice of a single working draft (the prompt inputs vs. the agent's metadata vs. which category
// it belongs to) but must be saved together as one record and reported as one save-state pill in
// the header. This composes the prompt-editing hook with the metadata + category slices on top, so
// the rest of the shell only has to deal with one draft.
function combineSaveState(promptSaveState: SaveState, metaDirty: boolean): SaveState {
	// Saving/Error already reflect the in-flight or failed save of the whole record (prompt inputs,
	// metadata and category are submitted together — see `save` below) — metadata dirtiness can't
	// change that verdict, only whether a *clean* state is `Saved` or `Unsaved`.
	if (promptSaveState === SaveState.Saving || promptSaveState === SaveState.Error) {
		return promptSaveState;
	}
	return promptSaveState === SaveState.Unsaved || metaDirty ? SaveState.Unsaved : SaveState.Saved;
}

export function useNovaAgentDraft(options: UseNovaAgentDraftOptions = {}): NovaAgentDraft {
	const {
		onSave,
		initialValues,
		initialCategoryId = null,
		initialTitle = '',
		initialDescription = '',
		initialModel = null,
	} = options;

	const [categoryId, setCategoryId] = useState<string | null>(initialCategoryId);
	const [baselineCategoryId, setBaselineCategoryId] = useState<string | null>(initialCategoryId);
	const [title, setTitle] = useState<string>(initialTitle);
	const [baselineTitle, setBaselineTitle] = useState<string>(initialTitle);
	const [description, setDescription] = useState<string>(initialDescription);
	const [baselineDescription, setBaselineDescription] = useState<string>(initialDescription);
	const [model, setModel] = useState<string | null>(initialModel);
	const [baselineModel, setBaselineModel] = useState<string | null>(initialModel);
	const [versionName, setVersionName] = useState<string>('');
	const [baselineVersionName, setBaselineVersionName] = useState<string>('');

	// `onSave` is recreated whenever the metadata slices change so the merged payload always carries
	// the values as of the moment `save()` is called, not stale ones captured at mount.
	const mergedOnSave = useCallback(
		async (values: PromptInputs) => {
			if (!onSave) {
				throw new Error('useNovaAgentDraft: no onSave provided');
			}
			await onSave({...values, categoryId, title, description, model, versionName});
		},
		[onSave, categoryId, title, description, model, versionName]
	);

	const promptState = useNovaPromptState({initialValues, onSave: mergedOnSave});

	const metaDirty =
		categoryId !== baselineCategoryId ||
		title !== baselineTitle ||
		description !== baselineDescription ||
		model !== baselineModel ||
		versionName !== baselineVersionName;
	const isDirty = promptState.isDirty || metaDirty;
	const saveState = combineSaveState(promptState.saveState, metaDirty);

	const save = useCallback(async () => {
		await promptState.save();
		// The prompt-state hook already reset its own baseline on success; only the metadata
		// baselines are ours to advance.
		setBaselineCategoryId(categoryId);
		setBaselineTitle(title);
		setBaselineDescription(description);
		setBaselineModel(model);
		setBaselineVersionName(versionName);
	}, [promptState, categoryId, title, description, model, versionName]);

	const load = useCallback(
		(
			inputs: PromptInputs,
			newCategoryId: string | null,
			newTitle: string,
			newDescription: string,
			newModel: string | null,
			newVersionName: string | null = null
		) => {
			promptState.load(inputs);
			setCategoryId(newCategoryId);
			setBaselineCategoryId(newCategoryId);
			setTitle(newTitle);
			setBaselineTitle(newTitle);
			setDescription(newDescription);
			setBaselineDescription(newDescription);
			setModel(newModel);
			setBaselineModel(newModel);
			setVersionName(newVersionName ?? '');
			setBaselineVersionName(newVersionName ?? '');
		},
		[promptState]
	);

	return {
		promptState,
		categoryId,
		setCategoryId,
		title,
		setTitle,
		description,
		setDescription,
		model,
		setModel,
		versionName,
		setVersionName,
		isDirty,
		saveState,
		save,
		load,
	};
}
